import { createSlice } from "@reduxjs/toolkit";

import {
  FE_HOST,
  GMT_TIME_IDENTIFIER,
  initialCalibrationListPaginationParams,
} from "@constants";
import { clearStore } from "actions/initialization";
import {
  calibrationUploadFileThunk,
  calibrationsListThunk,
  deleteCalibrationThunk,
  enableCalibrationThunk,
  enableAutoCalibrationThunk,
  fetchCalibrationThunk,
  renameCalibrationThunk,
} from "thunks/calibration";
import { getCsrfToken } from "utils";

const initialState = {
  ...initialCalibrationListPaginationParams,
  ids: [],
  hash: {},
  loading: {
    create: false,
    list: false,
    fetch: false,
    enable: false,
    delete: false,
    update: false,
  },
  error: {
    create: null,
    list: null,
    fetch: null,
    enable: null,
    delete: null,
    update: null,
  },
  uploadProgress: null,
  inProgress: false,
  isCreated: false,
};

const calibrationDTO = (calibration) => ({
  ...calibration,
  created_at: new Date(`${calibration.created_at}${GMT_TIME_IDENTIFIER}`),
  calibrated_at:
    new Date(`${calibration.calibrated_at}${GMT_TIME_IDENTIFIER}`) || null,
  originalUrl: calibration.url,
  url: calibration.url
    ? `${FE_HOST}${calibration.url}?token=${getCsrfToken()}`
    : undefined,
  loading: {
    fetch: false,
    update: false,
    delete: false,
  },
  errors: {
    fetch: null,
    update: null,
    delete: null,
  },
});

const disableEnabledCalibration = (state) => {
  for (const id of state.ids) {
    if (state.hash[id].enabled) {
      state.hash[id].enabled = false;
      break;
    }
  }
};

const calibrationSlice = createSlice({
  name: "calibration",
  initialState,
  reducers: {
    calibrationNotification(state, action) {
      const calibration = action.payload;
      state.inProgress = false;
      disableEnabledCalibration(state);
      state.hash[calibration.id] = calibrationDTO(calibration);
    },
    setUploadProgress(state, action) {
      const { value } = action.payload;
      state.uploadProgress = value;
    },
    calibrationsListSetPaginationData(state, action) {
      const { pagination } = action.payload;
      if (pagination) {
        const { count, currentPage, limit } = pagination;
        if (currentPage) {
          state.pagination.currentPage = currentPage;
          state.pagination.offset = (currentPage - 1) * state.pagination.limit;
        }
        if (count) {
          state.pagination.count = count;
        }
        if (limit) {
          state.pagination.limit = limit;
        }
        state.pagination.forceFetch = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // #region pending
      .addCase(calibrationUploadFileThunk.pending, (state) => {
        state.loading.create = true;
      })
      .addCase(calibrationsListThunk.pending, (state) => {
        state.loading.list = true;
      })
      .addCase(fetchCalibrationThunk.pending, (state, action) => {
        state.loading.fetch = true;
      })
      .addCase(enableCalibrationThunk.pending, (state) => {
        state.loading.enable = true;
      })
      .addCase(enableAutoCalibrationThunk.pending, (state) => {
        state.loading.enable = true;
      })
      .addCase(deleteCalibrationThunk.pending, (state, action) => {
        const {
          meta: {
            arg: { calibrationId },
          },
        } = action;
        if (state.hash[calibrationId]) {
          state.hash[calibrationId].loading.delete = true;
        }
      })
      .addCase(renameCalibrationThunk.pending, (state, action) => {
        const {
          meta: {
            arg: { calibrationId },
          },
        } = action;
        if (state.hash[calibrationId]) {
          state.hash[calibrationId].loading.update = true;
        }
      })
      // #endregion
      // #region fulfilled
      .addCase(calibrationUploadFileThunk.fulfilled, (state, action) => {
        state.loading.create = false;
        state.isCreated = true;
      })
      .addCase(calibrationsListThunk.fulfilled, (state, action) => {
        const {
          meta: {
            arg: { enabledCalibrationId },
          },
        } = action;
        state.loading.list = false;
        state.isCreated = false;
        const ids = new Set();
        if (enabledCalibrationId) {
          ids.add(enabledCalibrationId);
        }
        state.hash =
          enabledCalibrationId && state.hash[enabledCalibrationId]
            ? { [enabledCalibrationId]: state.hash[enabledCalibrationId] }
            : {};
        const { list, pagination } = action.payload;
        list.forEach((calibration) => {
          ids.add(calibration.id);
          state.hash[calibration.id] = calibrationDTO(calibration);
        });
        state.ids = Array.from(ids);
        const { limit, ...otherPaginationData } = pagination;
        state.pagination = {
          ...state.pagination,
          ...otherPaginationData,
          totalPages: Math.ceil(otherPaginationData.count / limit) || 1,
          forceFetch: false,
        };
      })
      .addCase(fetchCalibrationThunk.fulfilled, (state, action) => {
        const calibration = action.payload;
        state.loading.fetch = false;
        if (!state.ids.includes(calibration.id)) {
          state.ids.push(calibration.id);
        }
        state.hash[calibration.id] = calibrationDTO(calibration);
      })
      .addCase(enableCalibrationThunk.fulfilled, (state, action) => {
        const {
          meta: {
            arg: { calibrationId },
          },
        } = action;
        state.loading.enable = false;
        disableEnabledCalibration(state);
        state.hash[calibrationId].enabled = true;
      })
      .addCase(enableAutoCalibrationThunk.fulfilled, (state, action) => {
        state.loading.enable = false;
        disableEnabledCalibration(state);
        state.pagination.forceFetch = true;
      })
      .addCase(deleteCalibrationThunk.fulfilled, (state, action) => {
        const { id: calibrationId } = action.payload;
        state.hash[calibrationId].loading.delete = false;
        delete state.hash[calibrationId];
        state.ids = state.ids.filter((id) => id !== calibrationId);
        // sync pagination
        if (
          state.pagination.currentPage === state.pagination.totalPages &&
          state.pagination.currentPage > 1 &&
          state.ids.length === 0
        ) {
          state.pagination.currentPage = state.pagination.currentPage - 1;
          state.pagination.offset =
            (state.pagination.currentPage - 1) * state.pagination.limit;
        }
        if (
          state.pagination.currentPage !== state.pagination.totalPages &&
          state.ids.length < state.pagination.limit
        ) {
          state.pagination.forceFetch = true;
        }
        if (state.pagination.count > 0) {
          state.pagination.count = state.pagination.count - 1;
        }
      })
      .addCase(renameCalibrationThunk.fulfilled, (state, action) => {
        const {
          meta: {
            arg: { calibrationId },
          },
        } = action;
        const calibration = action.payload;
        if (state.hash[calibrationId]) {
          state.hash[calibrationId].loading.update = false;
        }
        if (calibration && state.hash[calibration.id]) {
          state.hash[calibration.id].name = calibration.name;
        }
      })
      // #endregion
      // #region rejected
      .addCase(calibrationUploadFileThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.create = false;
        state.error.create = error;
      })
      .addCase(calibrationsListThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.list = false;
        state.error.list = error;
      })
      .addCase(fetchCalibrationThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.fetch = false;
        state.error.fetch = error;
      })
      .addCase(enableCalibrationThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.enable = false;
        state.error.enable = error;
      })
      .addCase(enableAutoCalibrationThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.enable = false;
        state.error.enable = error;
      })
      .addCase(deleteCalibrationThunk.rejected, (state, action) => {
        const {
          meta: {
            arg: { calibrationId: id },
          },
          error,
        } = action;
        if (state.hash[id]) {
          state.hash[id].loading.delete = false;
          state.hash[id].errors.delete = error;
        }
      })
      .addCase(renameCalibrationThunk.rejected, (state, action) => {
        const {
          meta: {
            arg: { calibrationId },
          },
          error,
        } = action;
        if (state.hash[calibrationId]) {
          state.hash[calibrationId].loading.update = false;
          state.hash[calibrationId].errors.update = error;
        }
      })
      // #endregion
      .addCase(clearStore, () => initialState);
  },
});

export const actions = calibrationSlice.actions;

export default calibrationSlice.reducer;
